/**
 * Wholesale / custom order inquiry popup: opens from the quick-action
 * links, swaps its heading/copy per inquiry type, and submits straight
 * to EmailJS via fetch (no Shopify /contact post) so the modal flips to
 * a success message in place, with no page reload and no reCAPTCHA
 * interstitial.
 */
const EMAILJS_SERVICE_ID = 'service_f6vvp68';
const EMAILJS_TEMPLATE_ID = 'template_l5muu93';
const EMAILJS_PUBLIC_KEY = 'WvahKpzcuMxsIdFG8';

class InquiryModal {
  constructor() {
    this.modal = document.getElementById('InquiryModal');
    if (!this.modal) return;
    this.panel = this.modal.querySelector('.inquiry-modal__panel');
    this.titleEl = this.modal.querySelector('[data-inquiry-title]');
    this.textEl = this.modal.querySelector('[data-inquiry-text]');
    this.triggers = document.querySelectorAll('[data-inquiry-open]');

    this.bindTriggers();
    this.bindModalEvents();
    this.bindSubmit();
    this.bindPhoneFormatting();
  }

  bindPhoneFormatting() {
    const phoneInput = this.modal.querySelector('[data-inquiry-phone]');
    if (!phoneInput) return;
    phoneInput.addEventListener('input', () => {
      let digits = phoneInput.value.replace(/\D/g, '');
      if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
      digits = digits.slice(0, 10);

      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      }
      phoneInput.value = formatted;
    });
  }

  bindTriggers() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.resetForm();
        this.setContent(trigger.dataset);
        this.open();
      });
    });
  }

  setContent({ inquiryTitle, inquiryText, inquiryProduct }) {
    if (inquiryTitle && this.titleEl) this.titleEl.textContent = inquiryTitle;
    if (inquiryText && this.textEl) this.textEl.textContent = inquiryText;
    if (inquiryProduct) this.currentProduct = inquiryProduct;
  }

  resetForm() {
    const form = this.modal.querySelector('.inquiry-modal__form');
    const errorEl = this.modal.querySelector('[data-inquiry-error]');
    const successEl = this.modal.querySelector('[data-inquiry-success]');
    const submitButton = this.modal.querySelector('[data-inquiry-submit]');
    if (form) form.reset();
    if (errorEl) errorEl.hidden = true;
    if (successEl) successEl.hidden = true;
    if (submitButton) {
      submitButton.disabled = false;
      if (submitButton.dataset.originalText) submitButton.textContent = submitButton.dataset.originalText;
    }
  }

  bindSubmit() {
    const form = this.modal.querySelector('.inquiry-modal__form');
    const honeypot = this.modal.querySelector('[data-inquiry-honeypot]');
    const submitButton = this.modal.querySelector('[data-inquiry-submit]');
    const errorEl = this.modal.querySelector('[data-inquiry-error]');
    const successEl = this.modal.querySelector('[data-inquiry-success]');
    const phoneInput = this.modal.querySelector('[data-inquiry-phone]');
    const emailInput = this.modal.querySelector('[data-inquiry-email]');
    if (!form) return;

    form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (honeypot && honeypot.value.trim() !== '') {
        // Likely a bot: silently drop the submission.
        this.close();
        return;
      }

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      if (errorEl) errorEl.hidden = true;
      if (successEl) successEl.hidden = true;
      if (submitButton) {
        submitButton.dataset.originalText = submitButton.dataset.originalText || submitButton.textContent;
        submitButton.disabled = true;
        if (submitButton.dataset.sendingText) submitButton.textContent = submitButton.dataset.sendingText;
      }

      this.notifyByEmail({
        phone: phoneInput ? phoneInput.value : '',
        email: emailInput ? emailInput.value : '',
      })
        .then(() => {
          if (successEl) successEl.hidden = false;
        })
        .catch((error) => {
          console.error('Inquiry notification failed', error);
          if (submitButton) {
            submitButton.disabled = false;
            if (submitButton.dataset.originalText) submitButton.textContent = submitButton.dataset.originalText;
          }
          if (errorEl) errorEl.hidden = false;
        });
    });
  }

  notifyByEmail({ phone, email }) {
    return fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id: EMAILJS_SERVICE_ID,
        template_id: EMAILJS_TEMPLATE_ID,
        user_id: EMAILJS_PUBLIC_KEY,
        template_params: {
          inquiry_type: this.titleEl ? this.titleEl.textContent : '',
          product_title: this.currentProduct || '',
          page_url: window.location.href,
          phone: phone || '',
          email: email || '',
        },
      }),
    }).then((response) => {
      if (!response.ok) throw new Error(`Unexpected response: ${response.status}`);
    });
  }

  bindModalEvents() {
    this.modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
    this.modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-inquiry-close]')) this.close();
    });
  }

  open() {
    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => this.panel.querySelector('input, button')?.focus());
  }

  close() {
    this.modal.hidden = true;
    document.body.style.overflow = '';
  }
}

document.addEventListener('DOMContentLoaded', () => new InquiryModal());
